import { useCallback, useEffect, useRef, useState } from "react";
import { useAppFeedback } from "../useAppFeedback";
import dayjs, { type Dayjs } from "dayjs";
import { useAuth } from "../../context/AuthContext";
import { type DrinkActivitySummary, type DrinkOption } from "../../services/drinkService";
import { fetchAvailableDrinks, fetchDrinkActivities } from "../../services/drinks/DrinkService";
import { outbox } from "../../offline";
import { useTranslation } from "react-i18next";
import { useMetabolismStorage } from "./drinkStats/useMetabolismStorage";
import { useDrinkStatsDerived } from "./useDrinkStatsDerived";
import { useDrinkStatsEditActions } from "./useDrinkStatsEditActions";

const useDrinkStats = () => {
    const { message } = useAppFeedback();
    const { isAuthenticated, user } = useAuth();
    const { t } = useTranslation();
    const tRef = useRef(t);
    tRef.current = t;
    const [loading, setLoading] = useState(false);
    const [activitiesError, setActivitiesError] = useState<string | null>(null);
    const [activities, setActivities] = useState<DrinkActivitySummary[]>([]);
    const [selectedDay, setSelectedDay] = useState<Dayjs>(() => dayjs().startOf("day"));
    const [hasUserSelectedDay, setHasUserSelectedDay] = useState(false);
    const [availableDrinks, setAvailableDrinks] = useState<DrinkOption[]>([]);
    const [loadingDrinks, setLoadingDrinks] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        if (typeof window === "undefined") return;
        const intervalId = window.setInterval(() => setNowMs(Date.now()), 60 * 1000);
        return () => window.clearInterval(intervalId);
    }, []);

    const activitiesRequest = useRef(0);
    const loadActivities = useCallback(async () => {
        if (!user) return;
        const request = ++activitiesRequest.current;
        setLoading(true);
        setActivitiesError(null);
        try {
            const loaded = await fetchDrinkActivities(user);
            if (request === activitiesRequest.current) setActivities(loaded);
        } catch {
            if (request !== activitiesRequest.current) return;
            const details = tRef.current("drinkStats.messages.loadActivitiesError");
            setActivitiesError(details);
            message.error(details);
        } finally {
            if (request === activitiesRequest.current) setLoading(false);
        }
    }, [user, message]);

    useEffect(() => {
        if (isAuthenticated && user) {
            loadActivities();
        } else {
            activitiesRequest.current++;
            setActivities([]);
            setActivitiesError(null);
            setHasUserSelectedDay(false);
            setSelectedDay(dayjs().startOf("day"));
        }
    }, [isAuthenticated, user, loadActivities]);

    const { weightKg, setWeightKg, sex, setSex } = useMetabolismStorage(user?.userId ?? null);
    // A drink added from the drawer while this page is open: refresh instead of reloading the page.
    useEffect(() => outbox.subscribe((event) => event.type === "confirmed" && void loadActivities()), [loadActivities]);
    const loadAvailableDrinks = useCallback(async (): Promise<DrinkOption[]> => {
        if (!isAuthenticated) {
            setAvailableDrinks([]);
            return [];
        }
        setLoadingDrinks(true);
        try {
            const drinks = await fetchAvailableDrinks();
            setAvailableDrinks(drinks);
            return drinks;
        } catch {
            message.error(tRef.current("drinkStats.messages.loadAvailableError"));
            return [];
        } finally {
            setLoadingDrinks(false);
        }
    }, [isAuthenticated, message]);

    useEffect(() => { loadAvailableDrinks(); }, [loadAvailableDrinks]);

    const derived = useDrinkStatsDerived({ activities, availableDrinks, selectedDay, setSelectedDay,
        hasUserSelectedDay, weightKg, sex, nowMs, t });
    const editActions = useDrinkStatsEditActions({ user, isAuthenticated, availableDrinks,
        loadAvailableDrinks, loadActivities, t });

    return {
        isAuthenticated, user, t, loading, activities, activitiesError, selectedDay, setSelectedDay, setHasUserSelectedDay, loadActivities,
        weightKg, setWeightKg, sex, setSex, availableDrinks, loadingDrinks,
        ...derived, ...editActions,
    };
};

export default useDrinkStats;
